import React from "react";

const LEGEND_COLORS = [
  "#0d6efd",
  "#198754",
  "#dc3545",
  "#ffc107",
  "#6f42c1",
  "#fd7e14",
];

const ChartIcon: React.FC<{ onClick: (e: React.MouseEvent) => void }> = ({
  onClick,
}) => (
  <span
    onClick={onClick}
    style={{
      cursor: "pointer",
      marginLeft: 6,
      opacity: 0.4,
      display: "inline-flex",
      verticalAlign: "middle",
    }}
    title="View chart"
    onMouseEnter={(e) => {
      (e.currentTarget as HTMLElement).style.opacity = "0.9";
    }}
    onMouseLeave={(e) => {
      (e.currentTarget as HTMLElement).style.opacity = "0.4";
    }}
  >
    <svg viewBox="0 0 16 16" width="13" height="13" fill="#495057">
      <rect x="1" y="9" width="3" height="6" rx="0.5" />
      <rect x="6" y="5" width="3" height="10" rx="0.5" />
      <rect x="11" y="2" width="3" height="13" rx="0.5" />
    </svg>
  </span>
);

interface GridCellProps {
  values: number[];
  legends: string[];
  isTotal?: boolean;
  /** For multi-legend cells, called with the legend index; for single-legend, called with undefined */
  onClick?: (legendIndex?: number) => void;
  onChartClick?: () => void;
}

const GridCell: React.FC<GridCellProps> = ({
  values,
  legends,
  isTotal = false,
  onClick,
  onChartClick,
}) => {
  const isSingleLegend = legends.length <= 1;
  const hasValue = values.some((v) => v > 0);
  const isClickable = !isTotal && onClick && hasValue;

  const cellStyle: React.CSSProperties = {
    textAlign: "right",
    padding: "4px 8px",
    cursor: isClickable ? "pointer" : "default",
    fontWeight: isTotal ? 700 : 400,
    backgroundColor: isTotal ? "#f7f8fc" : undefined,
    whiteSpace: "nowrap",
    fontSize: "0.82rem",
  };

  const linkStyle: React.CSSProperties = {
    color: "var(--brand-primary)",
    textDecoration: "underline",
    textDecorationStyle: "dotted",
    textUnderlineOffset: "2px",
  };

  const handleChartClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChartClick?.();
  };

  if (isSingleLegend) {
    const val = values[0] ?? 0;
    return (
      <td style={cellStyle} onClick={isClickable ? () => onClick?.() : undefined}>
        {val > 0 ? (
          <span style={isClickable ? linkStyle : undefined}>
            {val.toLocaleString()}
          </span>
        ) : val === 0 ? (
          "-"
        ) : (
          val
        )}
        {onChartClick && hasValue && (
          <ChartIcon onClick={handleChartClick} />
        )}
      </td>
    );
  }

  // Multi-legend: each legend value is independently clickable
  return (
    <td style={{ ...cellStyle, lineHeight: 1.5, padding: "4px 8px", cursor: "default" }}>
      {values.map((val, idx) => {
        const legendClickable = !isTotal && onClick && val > 0;
        return (
          <div
            key={idx}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 4,
              fontSize: "0.78rem",
              cursor: legendClickable ? "pointer" : "default",
            }}
            onClick={legendClickable ? () => onClick(idx) : undefined}
          >
            <span
              style={{
                color: LEGEND_COLORS[idx % LEGEND_COLORS.length],
                fontWeight: 600,
                fontSize: "0.7rem",
                flexShrink: 0,
              }}
            >
              {legends[idx]}:
            </span>
            <span style={legendClickable ? linkStyle : undefined}>
              {val > 0 ? val.toLocaleString() : val === 0 ? "-" : val}
            </span>
          </div>
        );
      })}
      {onChartClick && hasValue && (
        <div style={{ textAlign: "right", marginTop: 2 }}>
          <ChartIcon onClick={handleChartClick} />
        </div>
      )}
    </td>
  );
};

export default GridCell;
